import { Trans, t } from '@lingui/macro'
import { Formik } from 'formik'
import { useContext, useEffect, useState } from 'react'
import * as Yup from 'yup'

import { SearchCombobox } from '@/@bothrs-ui/web/components/forms/search-combobox'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import {
  type DetailInputMode,
  StructuredObservation,
  useStructuredObservationDialog,
} from '@/modules/questionnaire/components/question-observation/structured-observation/StructuredObservation'
import { useAllergyCodeById } from '@/modules/questionnaire/hooks/useAllergyCodeById'
import { useAllergySearch } from '@/modules/questionnaire/hooks/useAllergySearch'
import { AllergyContext } from '../AllergyQuestion.context'
import { AllergyReactionStructuredInput, REACTION_INPUT_MIN_HEIGHT } from './AllergyReactionStructuredInput'

import type {
  AllergyCodeReaction,
  AllergyIntolerance,
  AllergySearchResult,
} from '@/modules/app/types/HealthParameter.types'

// ================================ Types ================================

export type AllergyFormValues = {
  name: string
  code: { system: string; value: string } | null
  note: string
  selectedReactions: AllergyCodeReaction[]
  reactionFreeText: string
}

// ================================ Validation ================================

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required(
      t({
        id: 'allergy.form.name.required',
        message: 'Allergienaam is verplicht',
      }),
    )
    .max(
      100,
      t({
        id: 'allergy.form.name.maxLength',
        message: 'Naam mag maximaal 100 tekens bevatten',
      }),
    ),
})

// ================================ Component ================================

export const AllergyItemForm = () => {
  const { allergies, addAllergy, editAllergy } = useContext(AllergyContext)!
  const { dialog, closeDialog } = useStructuredObservationDialog()

  const allergyToEdit = dialog.mode === 'update' ? (allergies.find((a) => a._clientId === dialog.itemId) ?? null) : null

  const initialSelected = allergyToEdit ? allergyToEdit.name : null

  const [selectedName, setSelectedName] = useState<string | null>(initialSelected)
  const [searchQuery, setSearchQuery] = useState('')
  const { results } = useAllergySearch(searchQuery)

  const { data: editAllergyCode } = useAllergyCodeById(allergyToEdit?.code?.value ?? null)

  // Fallback for legacy data without reactionMode: assume structured if any reaction has coded manifestations
  const canRestoreStructured =
    allergyToEdit != null &&
    allergyToEdit.reaction?.length > 0 &&
    (allergyToEdit.reactionMode === 'structured' ||
      (allergyToEdit.reactionMode == null &&
        allergyToEdit.reaction.some((r) => r.manifestation != null && r.manifestation.length > 0)))

  const restoredReactions: AllergyCodeReaction[] = canRestoreStructured
    ? allergyToEdit.reaction
        .filter((r) => r.manifestation != null && r.manifestation.length > 0 && r.manifestation[0].value != null)
        .map((r) => ({
          id: Number(r.manifestation![0].value),
          description: r.text || null,
          displayName: r.manifestation![0].displayName ?? null,
          severity: null,
        }))
    : []

  const initialReactionMode: DetailInputMode =
    allergyToEdit?.reactionMode ?? (canRestoreStructured ? 'structured' : allergyToEdit ? 'free' : 'structured')
  const [reactionMode, setReactionMode] = useState<DetailInputMode>(initialReactionMode)

  const [availableReactions, setAvailableReactions] = useState<AllergyCodeReaction[]>(restoredReactions)

  useEffect(() => {
    if (!editAllergyCode?.reactions) return
    setAvailableReactions(editAllergyCode.reactions)
  }, [editAllergyCode])

  const initialFreeText =
    allergyToEdit && !canRestoreStructured ? (allergyToEdit.reaction?.map((r) => r.text).join(', ') ?? '') : ''

  const initialValues: AllergyFormValues = allergyToEdit
    ? {
        name: allergyToEdit.name,
        code: allergyToEdit.code,
        note: allergyToEdit.note ?? '',
        selectedReactions: restoredReactions,
        reactionFreeText: initialFreeText,
      }
    : {
        name: '',
        code: null,
        note: '',
        selectedReactions: [],
        reactionFreeText: '',
      }

  const handleFormSubmit = (values: AllergyFormValues, { resetForm }: { resetForm: () => void }) => {
    let reactions: AllergyIntolerance['reaction']

    if (reactionMode === 'free') {
      reactions = values.reactionFreeText.trim() ? [{ text: values.reactionFreeText.trim() }] : []
    } else {
      reactions = values.selectedReactions.map((reaction) => ({
        text: reaction.description ?? '',
        manifestation: [
          {
            system: 'synops-alcode-reaction',
            value: String(reaction.id),
            display: reaction.description ?? '',
            displayName: reaction.displayName ?? null,
          },
        ],
      }))
    }

    // A reaction is required. The submit button is disabled when none is given,
    // but the form can still be submitted via Enter, so enforce it here too.
    if (reactions.length === 0) return

    const allergy: AllergyIntolerance = {
      synopsId: allergyToEdit?.synopsId ?? null,
      diffStatus: allergyToEdit?.diffStatus ?? 'inserted',
      name: values.name,
      code: values.code,
      updatedAt: new Date().toISOString(),
      note: values.note || undefined,
      reactionMode,
      reaction: reactions,
    }

    if (dialog.mode === 'update') {
      editAllergy(dialog.itemId, allergy)
    } else {
      addAllergy(allergy)
    }
    closeDialog()
    resetForm()
    setSelectedName(null)
  }

  return (
    <Formik
      key={dialog.mode === 'update' ? dialog.itemId : 'new'}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleFormSubmit}
    >
      {({ isValid, isSubmitting, values, setFieldValue }) => (
        <StructuredObservation.ItemFormLayout
          showDetailInput={!!selectedName}
          detailMode={reactionMode}
          onDetailModeChange={setReactionMode}
          showModeSwitch={availableReactions.length > 0}
          structuredInput={<AllergyReactionStructuredInput availableReactions={availableReactions} />}
          freeInput={
            <StructuredObservation.FreeInput
              fieldName='reactionFreeText'
              label={t({
                id: 'allergy.form.reactions.freeTextLabel',
                message: 'Beschrijf je reactie(s)',
              })}
              placeholder={t({
                id: 'allergy.form.reactions.freeTextPlaceholder',
                message: 'Bv.: Ik krijg uitslag en jeuk na het innemen',
              })}
              minHeight={REACTION_INPUT_MIN_HEIGHT}
            />
          }
          submitButton={
            <Button
              type='submit'
              variant='Primary'
              disabled={
                isSubmitting ||
                !isValid ||
                !values.name.trim() ||
                (reactionMode === 'free' ? !values.reactionFreeText.trim() : values.selectedReactions.length === 0)
              }
            >
              {allergyToEdit
                ? t({
                    id: 'allergy.form.saveButton',
                    message: 'Opslaan',
                  })
                : t({
                    id: 'allergy.form.addButton',
                    message: 'Allergie toevoegen',
                  })}
            </Button>
          }
        >
          {/* Allergy name search */}
          <SearchCombobox<AllergySearchResult>
            id='allergy-search'
            label={t({
              id: 'allergy.form.name.label',
              message: 'Naam allergie',
            })}
            placeholder={t({
              id: 'allergy.form.name.placeholder',
              message: 'Allergie',
            })}
            selectedName={selectedName}
            results={results}
            getResultName={(r) => r.name}
            freeTextLabel={(query) => (
              <>
                <Span type='BodyMediumBold'>{query}</Span>{' '}
                <Trans id='allergy.form.search.addFreeText'>toevoegen als nieuwe allergie</Trans>
              </>
            )}
            searchQuery={searchQuery}
            onSearchQueryChange={setSearchQuery}
            onSelect={(result) => {
              setSelectedName(result.name)
              setFieldValue('name', result.name)
              setFieldValue('code', {
                system: 'synops-alcode',
                value: result.id,
              })

              const reactions = result.reactions ?? []
              setAvailableReactions(reactions)

              setFieldValue('selectedReactions', [])
              setReactionMode(reactions.length > 0 ? 'structured' : 'free')
            }}
            onSelectFreeText={(name) => {
              setSelectedName(name)
              setFieldValue('name', name)
              setFieldValue('code', null)
              setAvailableReactions([])
              setReactionMode('free')
            }}
            onClear={() => {
              setSelectedName(null)
              setFieldValue('name', '')
              setFieldValue('code', null)
              setAvailableReactions([])
            }}
          />
        </StructuredObservation.ItemFormLayout>
      )}
    </Formik>
  )
}
